import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");
  const [consentError, setConsentError] = useState("");

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setConsentError("");
    setStatus("idle");

    if (!validateEmail(email)) {
      setEmailError("Zadajte platnú e-mailovú adresu.");
      return;
    }

    if (!gdprConsent) {
      setConsentError("Pre prihlásenie je potrebný súhlas.");
      return;
    }

    setIsLoading(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/subscribe-mailerlite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (data.ok) {
        setStatus("success");
        setEmail("");
        setGdprConsent(false);
      } else {
        console.error("Newsletter subscription error:", data.error);
        setStatus("error");
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  if (status === "success") {
    return (
      <div className="flex items-start gap-3 text-green-400">
        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm">Ďakujem za prihlásenie! Skontrolujte si email.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-gold" aria-hidden="true" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-white/60">
          Odber noviniek
        </h3>
      </div>

      <p className="text-sm text-white/70 mb-4 max-w-sm">
        Praktické AI tipy priamo do emailu. Žiadny spam.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            placeholder="Váš e-mail"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
              setStatus("idle");
            }}
            disabled={isLoading}
            className="flex-1 h-11 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-all disabled:opacity-50"
            aria-label="E-mailová adresa"
            aria-invalid={emailError ? "true" : undefined}
            aria-describedby={emailError ? "footer-email-error" : undefined}
          />
          <motion.button
            type="submit"
            disabled={isLoading}
            className="h-11 px-5 rounded-lg bg-gold text-charcoal font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gold-light transition-all disabled:opacity-50 whitespace-nowrap"
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                <span>Odosielam...</span>
              </>
            ) : (
              <span>Prihlásiť sa</span>
            )}
          </motion.button>
        </div>

        {emailError && (
          <p id="footer-email-error" role="alert" className="text-red-300 text-xs">
            {emailError}
          </p>
        )}

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="footer-newsletter-gdpr"
            checked={gdprConsent}
            onChange={(e) => {
              setGdprConsent(e.target.checked);
              setConsentError("");
            }}
            disabled={isLoading}
            className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/10 text-gold focus:ring-gold cursor-pointer"
          />
          <label
            htmlFor="footer-newsletter-gdpr"
            className="text-xs text-white/60 cursor-pointer leading-tight"
          >
            Súhlasím so spracovaním e-mailu na účely zasielania newslettera.{" "}
            <Link to="/gdpr" className="text-gold hover:underline underline-offset-2">
              Viac informácií
            </Link>
          </label>
        </div>

        {consentError && (
          <p role="alert" className="text-red-300 text-xs">
            {consentError}
          </p>
        )}

        {status === "error" && (
          <p role="alert" className="text-red-300 text-xs">
            Prihlásenie sa nepodarilo. Skúste to prosím znova.
          </p>
        )}
      </form>
    </div>
  );
}

export default FooterNewsletter;
